import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopoverMenu from 'src/common/PopoverMenu/PopoverMenu';
import PopoverMenuItem from 'src/common/PopoverMenu/PopoverMenuItem';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const ChatHeaderActions = ({
  conversation,
  onAddRecipients,
  onFinishConversation,
  onVerificationRequest,
}) => {
  const [loading, setLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const handleVerificationRequest = async () => {
    if (!onVerificationRequest || verificationLoading) return;
    setVerificationLoading(true);
    try {
      const result = await onVerificationRequest();
      if (result && !result.success) {
        const key =
          result.error === 'NO_ELIGIBLE_MEMBERS'
            ? 'monitoring.verification.error.no_eligible_members'
            : result.error === 'NO_CODES_CONFIGURED'
              ? 'monitoring.verification.error.no_codes_configured'
              : result.error === 'VERIFICATION_REQUEST_PENDING'
                ? 'monitoring.verification.error.request_pending'
                : 'monitoring.verification.error.generic';
        enqueueSnackbar({
          message: t(key),
          variant: 'warning',
          // These messages explain how to configure codes, so they are longer
          // than a usual toast. Cap the width and let them wrap instead of
          // stretching across the viewport.
          style: { maxWidth: 700 },
        });
      }
    } finally {
      setVerificationLoading(false);
    }
  };

  const confirmFinishConversation = () =>
    confirm({
      title: t('chat.finish_chat_confirmation'),
      description: t('chat.finish_chat_description'),
    })
      .then(async () => {
        setLoading(true);
        try {
          await onFinishConversation();
        } catch (error) {
          enqueueSnackbar({
            message: t('chat.finish_chat_error'),
            variant: 'error',
          });
        } finally {
          setLoading(false);
        }
      })
      .catch(() => null);

  return (
    <>
      {onVerificationRequest && (
        <Tooltip title={t('monitoring.verification.button.tooltip')}>
          <IconButton
            color="success"
            onClick={handleVerificationRequest}
            disabled={verificationLoading}
          >
            {verificationLoading ? (
              <CircularProgress size={20} />
            ) : (
              <VerifiedUserIcon width={20} height={20} />
            )}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t('chat.add_participants_tooltip')}>
        <IconButton onClick={() => onAddRecipients()}>
          <GroupAddIcon width={20} height={20} />
        </IconButton>
      </Tooltip>
      {!conversation.situationId && (
        <PopoverMenu>
          <PopoverMenuItem
            title={t('chat.finish_chat_option')}
            onClick={confirmFinishConversation}
            disabled={loading}
            {...(loading && { icon: CircularProgress })}
          />
        </PopoverMenu>
      )}
    </>
  );
};

export default ChatHeaderActions;
